import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from 'react-i18next';

// Revenue Department designations
const REVENUE_DESIGNATIONS = [
  { value: "tahsildar", labelKey: "designation.tahsildar" },
  { value: "deputy_tahsildar", labelKey: "designation.deputy_tahsildar" },
  { value: "shirestedar", labelKey: "designation.shirestedar" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "first_division_clerk", labelKey: "designation.first_division_clerk" },
  { value: "second_division_clerk", labelKey: "designation.second_division_clerk" },
];

// Survey Department designations
const SURVEY_DESIGNATIONS = [
  { value: "ddlr", labelKey: "designation.ddlr" },
  { value: "adlr", labelKey: "designation.adlr" },
  { value: "surveyor", labelKey: "designation.surveyor" },
];

const DEPARTMENTS = [
  { value: "revenue", labelKey: "department.revenue" },
  { value: "survey", labelKey: "department.survey" },
];

export default function EditOfficialDialog({ 
  open, 
  onOpenChange, 
  official, 
  userDepartment,
  onSave 
}) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [department, setDepartment] = useState("");
  const [designation, setDesignation] = useState("");

  // Initialize form when dialog opens with official data
  useEffect(() => {
    if (open && official) {
      setName(official.name || "");
      setPhone(official.phone || "");
      setPassword("");
      setDepartment(userDepartment || official.department || "");
      setDesignation(official.designation || "");
      setShowPassword(false);
    }
  }, [open, official, userDepartment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name,
      phone,
      password: password.trim() || undefined,
      department,
      designation
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('officialsManagement.editOfficial')}</DialogTitle>
          <DialogDescription>
            {official?.email}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t('officialsManagement.name')}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1"
              data-testid="edit-official-name"
            />
          </div>
          <div>
            <Label>{t('officialsManagement.phone')}</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="mt-1"
              data-testid="edit-official-phone"
            />
          </div>
          <div>
            <Label>{t('officialsManagement.newPassword')}</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('officialsManagement.leaveBlankToKeep')}
                data-testid="edit-official-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                data-testid="toggle-edit-password-visibility"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">{t('officialsManagement.passwordHint')}</p>
          </div>
          <div>
            <Label>{t('officialsManagement.department')}</Label>
            <Select 
              value={department} 
              onValueChange={(value) => {
                setDepartment(value);
                setDesignation("");
              }}
              disabled={true}
            >
              <SelectTrigger className="mt-1 bg-stone-100" data-testid="edit-official-department">
                <SelectValue placeholder={t('department.selectDepartment')} />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">{t('officialsManagement.departmentLockedToYours')}</p>
          </div>
          <div>
            <Label>{t('officialsManagement.designation')}</Label>
            <Select 
              value={designation} 
              onValueChange={setDesignation}
              disabled={!department}
            >
              <SelectTrigger className="mt-1" data-testid="edit-official-designation">
                <SelectValue placeholder={department ? t('designation.selectDesignation') : t('designation.selectDepartmentFirst')} />
              </SelectTrigger>
              <SelectContent>
                {department === "revenue" && REVENUE_DESIGNATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
                {department === "survey" && SURVEY_DESIGNATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('officialsManagement.cancel')}
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-edit-official">
              {t('officialsManagement.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
